import React from "react";
import {
  Button,
  Dropdown,
  Modal,
  Radio,
} from "@hid-galaxy-ui/galaxy-react";
import { useAppDispatch, useAppSelector } from "../../../../../redux";
import {
  addValidationStatusSagaAction,
  updateValidationStatusSagaAction,
} from "../../../../../redux/saga/validation-status/saga-actions";
import { ValidationStatusRow } from "../table/ValidationStatusTable";
import "./ValidationStatusFormModal.scss";

type Props = {
  setModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
  mode: "Add" | "Update";
  row?: ValidationStatusRow | null;
  onActionSuccess?: (message: string) => void;
  onActionError?: (message: string) => void;
};

const ValidationStatusFormModal: React.FC<Props> = ({
  setModalOpen,
  mode,
  row,
  onActionSuccess,
  onActionError,
}) => {
  const dispatch = useAppDispatch();
  const { predefinedData, list, status } = useAppSelector(
    (state) => state.validation_status
  );
  const [partnerName, setPartnerName] = React.useState<string>(
    row?.partner_name || ""
  );
  const [technology, setTechnology] = React.useState<string>(row?.technology || "");
  const [integration, setIntegration] = React.useState<string>(
    row?.integration || ""
  );
  const [validationStatus, setValidationStatus] = React.useState<"1" | "0">(
    row?.validation_status === 1 ? "1" : "0"
  );

  const partnerOptions = React.useMemo(
    () =>
      predefinedData.partners.map((p) => ({
        label: p.partner_name,
        value: p.partner_name,
      })),
    [predefinedData.partners]
  );

  const technologyOptions = React.useMemo(() => {
    const fromAPI = predefinedData.technologies || [];
    const fallback = Array.from(new Set(list.map((item) => item.technology)));
    const values = fromAPI.length ? fromAPI : fallback;
    return values.map((v) => ({ label: v, value: v }));
  }, [predefinedData.technologies, list]);

  const integrationOptions = React.useMemo(() => {
    const fromAPI = predefinedData.integrations || [];
    const fallback = Array.from(new Set(list.map((item) => item.integration)));
    const values = fromAPI.length ? fromAPI : fallback;
    return values.map((v) => ({ label: v, value: v }));
  }, [predefinedData.integrations, list]);

  const isSubmitDisabled = !partnerName || !technology || !integration;

  const onSubmit = () => {
    if (mode === "Add") {
      dispatch(
        addValidationStatusSagaAction({
          body: {
            partner_name: partnerName,
            technology,
            integration,
            validation_status: validationStatus === "1" ? 1 : 0,
          },
          onSuccess: () => {
            onActionSuccess?.("Partner added successfully.");
            setModalOpen(false);
          },
          onError: (message) => onActionError?.(message),
        })
      );
      return;
    }

    if (!row?.ID) return;
    dispatch(
      updateValidationStatusSagaAction({
        ID: row.ID,
        body: {
          technology,
          integration,
          validation_status: validationStatus === "1" ? 1 : 0,
        },
        onSuccess: () => {
          onActionSuccess?.("Partner updated successfully.");
          setModalOpen(false);
        },
        onError: (message) => onActionError?.(message),
      })
    );
  };

  return (
    <Modal
      headerText={mode === "Add" ? "Add Partner" : "Update Partner"}
      width={560}
      onClose={() => setModalOpen(false)}
      className="validation-status-form-modal"
    >
      <div className="validation-status-form-content">
        <div className="label-field-container">
          <label className="field-label">
            Partner Name<span>*</span>
          </label>
          <Dropdown
            id="partnerName"
            name="partnerName"
            placeholder="Select Partner Name"
            options={partnerOptions}
            selected={
              partnerName ? { label: partnerName, value: partnerName } : null
            }
            isSearchable={true}
            onSelect={(data) => setPartnerName(data?.value || "")}
            isRequired={true}
            theme="dark"
            isDisabled={mode === "Update"}
          />
        </div>

        <div className="label-field-container">
          <label className="field-label">
            Technology<span>*</span>
          </label>
          <Dropdown
            id="technology"
            name="technology"
            placeholder="Select Technology"
            options={technologyOptions}
            selected={technology ? { label: technology, value: technology } : null}
            isSearchable={true}
            onSelect={(data) => setTechnology(data?.value || "")}
            isRequired={true}
            theme="dark"
          />
        </div>

        <div className="label-field-container">
          <label className="field-label">
            Integration<span>*</span>
          </label>
          <Dropdown
            id="integration"
            name="integration"
            placeholder="Select Integration"
            options={integrationOptions}
            selected={
              integration ? { label: integration, value: integration } : null
            }
            isSearchable={true}
            onSelect={(data) => setIntegration(data?.value || "")}
            isRequired={true}
            theme="dark"
          />
        </div>

        <div className="label-field-container status-radio">
          <Radio
            selectedValue={validationStatus}
            legend="Status"
            groupName="validationStatus"
            radioList={[
              { key: "1", value: "True" },
              { key: "0", value: "False" },
            ]}
            onChange={(_e, key) => setValidationStatus(key as "1" | "0")}
            isRequired={true}
          />
        </div>

        <div className="button-group">
          <Button
            variant="secondary"
            label="Cancel"
            onClick={() => setModalOpen(false)}
          />
          <Button
            variant="primary"
            label={mode === "Add" ? "Add" : "Update"}
            onClick={onSubmit}
            disabled={
              isSubmitDisabled || status.addLoading || status.updateLoading
            }
          />
        </div>
      </div>
    </Modal>
  );
};

export default ValidationStatusFormModal;

